import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://kakobuyspreadsheet.website";
const FAVICON_URL = "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png";

export default function BagCollectionPage() {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Top Kakobuy Spreadsheet Bag Collection: Designer & Casual Bags 2026",
    "description": "Browse our handpicked bag collection from the Kakobuy spreadsheet featuring everything from luxury handbags to everyday backpacks with verified quality and seller ratings.",
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Team"
    },
    "datePublished": "2026-06-25",
    "dateModified": "2026-06-25",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": SITE_URL + "/guide/bag-collection"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet",
      "logo": {
        "@type": "ImageObject",
        "url": FAVICON_URL
      }
    },
    "image": "https://readdy.ai/api/search-image?query=A%20beautiful%20structured%20camel%20leather%20tote%20bag%20and%20a%20minimalist%20canvas%20backpack%20displayed%20side%20by%20side%20on%20a%20light%20marble%20surface%20with%20a%20small%20decorative%20plant%20in%20the%20background%20soft%20natural%20daylight%20from%20a%20window%20on%20the%20left%20luxury%20product%20photography%20with%20elegant%20warm%20composition&width=1400&height=600&seq=article-bags-v2-06&orientation=landscape"
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-foreground-900 transition-colors mb-6 cursor-pointer">
              <i className="ri-arrow-left-line text-sm"></i>Back to Kakobuy Spreadsheet Home
            </Link>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold mb-4">Bag Collection</span>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4 text-balance">
              Top <span className="text-primary-500">Kakobuy Spreadsheet</span> Bag Collection: Designer &amp; Casual
            </h1>
            <div className="flex items-center gap-4 text-xs text-foreground-500 mb-8 pb-6 border-b border-background-200/70">
              <span>Last Updated: June 25, 2026</span><span className="w-1 h-1 rounded-full bg-foreground-400"></span><span>5 min read</span>
            </div>
          </div>

          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <div className="relative rounded-2xl overflow-hidden aspect-[21/9] mb-8">
              <img src="https://readdy.ai/api/search-image?query=A%20beautiful%20structured%20camel%20leather%20tote%20bag%20and%20a%20minimalist%20canvas%20backpack%20displayed%20side%20by%20side%20on%20a%20light%20marble%20surface%20with%20a%20small%20decorative%20plant%20in%20the%20background%20soft%20natural%20daylight%20from%20a%20window%20on%20the%20left%20luxury%20product%20photography%20with%20elegant%20warm%20composition&width=1400&height=600&seq=article-bags-v2-06&orientation=landscape"
                alt="Kakobuy Spreadsheet Bag Collection" className="w-full h-full object-cover rounded-2xl" loading="lazy" />
            </div>

            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">Bags: A Growing Category in Kakobuy Spreadsheet</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                The bag category in our <strong>Kakobuy Spreadsheet</strong> has grown to over 1,200 verified listings, covering everything from luxury-inspired handbags to practical everyday backpacks and crossbody bags. This category has seen remarkable quality improvements over the past year, with several sellers now producing items that rival premium retail brands.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                Our <strong>Kakobuy Spreadsheet</strong> bag section is organized by type — handbags, backpacks, crossbody bags, totes, and travel bags — making it easy to find exactly what you are looking for. Each listing includes detailed material information, hardware quality assessments, and interior organization photos so you know exactly what to expect.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">What to Look for in Bag QC Photos</h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                When reviewing bag listings in the <strong>Kakobuy Spreadsheet</strong>, the QC photos are your most valuable resource. Pay attention to stitching evenness along seams, hardware finish and weight, zipper smoothness, and interior lining quality. These details separate high-quality bags from disappointing ones.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                The <strong>Kakobuy Spreadsheet</strong> includes comparison notes for popular bag styles, showing how different sellers' versions of the same design compare in terms of materials, accuracy, and overall craftsmanship. This feature alone can save you from ordering an inferior version of a bag you have been eyeing.
              </p>
            </section>

            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-semibold rounded-lg hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer">
                <i className="ri-home-4-line text-sm"></i>Back to Homepage
              </Link>
              <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-foreground-200 text-foreground-700 text-sm font-medium rounded-lg hover:border-foreground-400 transition-all duration-300 whitespace-nowrap cursor-pointer">
                Browse Spreadsheet <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}